import { useState } from 'react';
import { aplicarCodigoConvite } from '../../../../lib/verificarAcessoGratuito';
import { supabase } from '../../../../lib/supabaseClient';

interface CodigoConviteModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function CodigoConviteModal({ onClose, onSuccess }: CodigoConviteModalProps) {
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);

  const aplicarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!codigo.trim()) {
      setMensagem({ tipo: 'erro', texto: 'Digite um código válido' });
      return;
    }

    setLoading(true);
    setMensagem(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setMensagem({ tipo: 'erro', texto: 'Você precisa estar logado para aplicar um código' });
        setLoading(false);
        return;
      }

      const resultado = await aplicarCodigoConvite(codigo.trim(), user.id);

      if (resultado.temAcesso) {
        setMensagem({ tipo: 'sucesso', texto: resultado.mensagem });
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setMensagem({ tipo: 'erro', texto: resultado.mensagem });
      }
    } catch (error: any) {
      console.error('Erro ao aplicar código:', error);
      setMensagem({ tipo: 'erro', texto: 'Erro inesperado ao aplicar código. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Aplicar Código de Convite</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-12 h-12 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={aplicarCodigo} className="space-y-5">
          <div>
            <label className="block text-xl font-medium text-gray-700 mb-2">
              Código de Convite
            </label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              placeholder="ABC123XYZ"
              disabled={loading}
              className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent uppercase font-mono text-2xl text-center disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
              maxLength={20}
              autoFocus
            />
            <p className="text-lg text-gray-500 mt-2">
              Digite o código que você recebeu
            </p>
          </div>

          {mensagem && (
            <div className={`p-4 rounded-lg border animate-slide-down ${
              mensagem.tipo === 'sucesso'
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                <i className={`${
                  mensagem.tipo === 'sucesso' 
                    ? 'ri-checkbox-circle-line text-green-600' 
                    : 'ri-error-warning-line text-red-600'
                } text-2xl flex-shrink-0`}></i>
                <p className={`text-xl ${
                  mensagem.tipo === 'sucesso' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {mensagem.texto}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-xl"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !codigo.trim()}
              className="flex-1 px-6 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-md hover:shadow-lg text-xl"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-2xl"></i>
                  Aplicando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-check-line text-2xl"></i>
                  Aplicar Código
                </span>
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-teal-50 rounded-lg p-5">
            <div className="flex gap-3">
              <i className="ri-information-line text-2xl text-teal-600 flex-shrink-0"></i>
              <div>
                <p className="text-xl font-medium text-teal-900 mb-1">
                  Como funciona?
                </p>
                <p className="text-lg text-teal-700">
                  Ao aplicar um código válido, você receberá acesso gratuito completo à plataforma. O código só pode ser usado uma vez.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
